/*
PURPOSE: Grid layout component for displaying user's coral contributions
USE CASES:
- Display user's corals in organized cards with artist grids
- Show contribution details like pledge amount and join date
- Provide visual separation between different corals
DOMAIN BOUNDARIES:
- Responsible for layout and organization of coral cards
- Delegates individual coral rendering to CoralCard
- Does not handle data fetching
ASSUMPTIONS:
- Receives filtered coral data from parent component
- Each coral should be displayed as a separate card
- Grid should be responsive across different screen sizes
*/

import React from 'react';
import { FilteredClientCoral, ClientCoral } from 'shared/types/platformTypes';
import CoralCard from './CoralCard';

interface CoralGridProps {
  corals: FilteredClientCoral[] | ClientCoral[];
  isLoading?: boolean;
  userName?: string;
  variant?: 'dashboard' | 'profile';
  showSubscriptionFeatures?: boolean;
  enableCoralNavigation?: boolean;
}

const CoralGrid: React.FC<CoralGridProps> = ({
  corals,
  isLoading = false,
  userName,
  variant = 'profile',
  showSubscriptionFeatures = false,
  enableCoralNavigation = false
}) => {
  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mb-6"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 8 }).map((_, artistIndex) => (
                <div key={artistIndex} className="aspect-square bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Show empty state
  if (corals.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </div>
        <p className="text-gray-500 text-lg mb-2">No corals to display</p>
        <p className="text-gray-400 text-sm">
          {userName ? `${userName} hasn't shared any corals yet.` : "No corals found."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {corals.map((coral) => (
        <CoralCard
          key={coral.guid}
          coral={coral}
          variant={variant}
          showSubscriptionFeatures={showSubscriptionFeatures}
          enableCoralNavigation={enableCoralNavigation}
        />
      ))}
    </div>
  );
};

export default CoralGrid;